import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { ReportErrorButton } from "@/components/ReportError";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { ChartFrame, RankingChart, TrendLineChart, TrendBadge, CompareChart } from "@/components/charts";
import { TrendingUp, BarChart3, Megaphone, Quote, MessageSquareText, GitCompareArrows, Mail } from "lucide-react";

const RANGES = [
  { id: "7d", label: "Last 7 Days" },
  { id: "30d", label: "30 Days" },
  { id: "90d", label: "90 Days" },
  { id: "election", label: "Election Period" },
  { id: "all", label: "All Time" },
];

export default function CommunityVoice() {
  const [params, setParams] = useSearchParams();
  const [munis, setMunis] = useState([]);
  const [muniId, setMuniId] = useState(params.get("municipality") || "");
  const [range, setRange] = useState("30d");
  const [top, setTop] = useState(null);
  const [trend, setTrend] = useState(null);
  const [youSaid, setYouSaid] = useState(null);
  const [themes, setThemes] = useState(null);
  const [voice, setVoice] = useState(null);
  const [loading, setLoading] = useState(true);
  // compare tool
  const [cmpB, setCmpB] = useState("");
  const [cmpAWard, setCmpAWard] = useState("");
  const [eligibleWards, setEligibleWards] = useState([]);
  const [cmp, setCmp] = useState(null);
  const [cmpBusy, setCmpBusy] = useState(false);
  // weekly digest subscribe (place-based only)
  const [digestEmail, setDigestEmail] = useState("");
  const [digestMuni, setDigestMuni] = useState("");
  const [digestWard, setDigestWard] = useState("");
  const [digestBusy, setDigestBusy] = useState(false);

  const subscribeDigest = async (e) => {
    e.preventDefault();
    setDigestBusy(true);
    try {
      const r = await api.post("/public/digest/subscribe", {
        email: digestEmail, municipality_id: digestMuni,
        ward: digestWard.trim() || null, website: "",
      });
      toast.success(r.data.message || "Subscribed!");
      setDigestEmail(""); setDigestWard("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Subscription failed");
    } finally {
      setDigestBusy(false);
    }
  };

  useEffect(() => {
    setEligibleWards([]);
    setCmpAWard("");
    if (!muniId) return;
    api.get("/public/intelligence/pulse", { params: { municipality_id: muniId } })
      .then((r) => setEligibleWards((r.data.ward_pulses || []).map((w) => w.ward)))
      .catch(() => {});
  }, [muniId]);

  useEffect(() => {
    api.get("/public/municipalities").then((r) => setMunis(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setTop(null); setTrend(null); setYouSaid(null); setThemes(null); setVoice(null);
    const q = { range };
    if (muniId) q.municipality_id = muniId;
    Promise.allSettled([
      api.get("/public/intelligence/top-issues", { params: q }).then((r) => setTop(r.data)),
      api.get("/public/intelligence/trending", { params: q }).then((r) => setTrend(r.data)),
      api.get("/public/intelligence/you-said", { params: q }).then((r) => setYouSaid(r.data)),
      api.get("/public/intelligence/themes", { params: muniId ? { municipality_id: muniId } : {} }).then((r) => setThemes(r.data)),
      api.get("/public/community-voice", { params: q }).then((r) => setVoice(r.data)),
    ]).finally(() => setLoading(false));
  }, [muniId, range]);

  useEffect(() => { load(); }, [load]);

  const runCompare = async () => {
    if (!muniId || !cmpB) return;
    setCmpBusy(true);
    try {
      const params = { a_municipality_id: muniId, range };
      if (cmpAWard) params.a_ward = cmpAWard;
      params.b_municipality_id = cmpB === "citywide" ? muniId : cmpB;
      const r = await api.get("/public/intelligence/compare", { params });
      setCmp(r.data);
    } catch (e) {
      setCmp({ available: false, note: e.response?.data?.detail || "Comparison unavailable." });
    } finally {
      setCmpBusy(false);
    }
  };

  const muniName = munis.find((m) => m.id === muniId)?.name;

  return (
    <div className="wyv-container py-10 sm:py-14">
      <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 mb-3">Community Intelligence</Badge>
      <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="community-voice-title">
        What people are talking about
      </h1>
      <p className="text-muted-foreground mt-2 max-w-2xl">
        Tell us what matters where you live. See what other participating residents are saying
        {muniName ? ` — ${muniName}` : " across Ontario"}.
      </p>

      <div className="flex flex-wrap items-end gap-4 mt-6">
        <div className="w-full sm:w-64">
          <Label className="text-xs text-muted-foreground">Municipality</Label>
          <Select value={muniId || "all"} onValueChange={(v) => { const val = v === "all" ? "" : v; setMuniId(val); setCmp(null); if (val) setParams({ municipality: val }); else setParams({}); }}>
            <SelectTrigger className="mt-1 bg-white" data-testid="voice-muni-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All of Ontario</SelectItem>
              {munis.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Tabs value={range} onValueChange={(v) => { setRange(v); setCmp(null); }} className="max-w-full min-w-0">
          <TabsList className="h-auto flex-wrap justify-start" data-testid="voice-range-tabs">
            {RANGES.map((r) => <TabsTrigger key={r.id} value={r.id} className="text-xs sm:text-sm" data-testid={`voice-range-${r.id}`}>{r.label}</TabsTrigger>)}
          </TabsList>
        </Tabs>
      </div>

      {/* YOU SAID — template-generated from real statistics only */}
      {youSaid?.available && (
        <Card className="mt-8 border-l-4 border-l-accent" data-testid="you-said-card">
          <CardContent className="p-5 sm:p-6">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5 inline-flex items-center gap-1.5">
              <Quote className="h-3.5 w-3.5 text-accent" /> You said — here's what we're hearing
            </p>
            <p className="font-heading font-semibold text-foreground text-base sm:text-lg" data-testid="you-said-text">{youSaid.text}</p>
            <p className="text-xs text-muted-foreground mt-1.5" data-testid="you-said-basis">{youSaid.based_on}</p>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8"><Skeleton className="h-72" /><Skeleton className="h-72" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8 items-start">
          {/* TOP ISSUES */}
          <Card data-testid="top-issues-card">
            <CardContent className="p-5 sm:p-6">
              <h2 className="font-heading font-bold text-lg text-foreground inline-flex items-center gap-2 mb-3">
                <BarChart3 className="h-5 w-5 text-accent" /> Top Issues{muniName ? ` — ${muniName}` : ""}
              </h2>
              {top?.available ? (
                <>
                  <ChartFrame responses={top.response_count} testId="top-issues-chart-frame"
                              footnote={`${top.contributing_polls} poll${top.contributing_polls === 1 ? "" : "s"} contributing · last updated ${top.last_updated ? new Date(top.last_updated).toLocaleDateString() : "—"}`}>
                    <RankingChart items={top.items} testId="top-issues-ranking" />
                  </ChartFrame>
                  <div className="mt-3 space-y-1.5" data-testid="top-issues-list">
                    {top.items.slice(0, 5).map((i) => (
                      <div key={i.label} className="flex items-center justify-between gap-2 text-sm flex-wrap">
                        <span className="text-foreground">{i.label}
                          <span className="text-muted-foreground text-xs tabular-nums"> · {i.count} ({i.percent}%)</span>
                        </span>
                        <TrendBadge trend={i.trend} />
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="top-issues-threshold-note">
                  {top?.note || "Not enough responses yet in this area — results appear once the privacy minimum is met."}
                </p>
              )}
            </CardContent>
          </Card>

          <div className="space-y-5 min-w-0">
            {/* WHAT'S CHANGING */}
            <Card data-testid="trending-card">
              <CardContent className="p-5 sm:p-6">
                <h2 className="font-heading font-bold text-lg text-foreground inline-flex items-center gap-2 mb-1">
                  <TrendingUp className="h-5 w-5 text-accent" /> What's Changing?
                </h2>
                {trend?.available ? (
                  <>
                    <p className="text-[11px] text-muted-foreground mb-3">{trend.period_note}</p>
                    <ul className="space-y-2" data-testid="trending-list">
                      {trend.items.map((t) => (
                        <li key={t.label} className="flex items-center justify-between gap-2 text-sm flex-wrap">
                          <span className="text-foreground">{t.label}</span>
                          <TrendBadge trend={t} testId={`trending-${t.direction}-${t.label.replace(/\W+/g, "-").toLowerCase()}`} />
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground" data-testid="trending-threshold-note">
                    {trend?.note || "Not enough data yet"}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Responses over time */}
            {top?.available && top.series?.some((s) => s.responses > 0) && (
              <Card data-testid="timeseries-card">
                <CardContent className="p-5 sm:p-6">
                  <h2 className="font-heading font-bold text-base text-foreground mb-2">Participation over time</h2>
                  <ChartFrame responses={top.response_count} testId="timeseries-frame">
                    <TrendLineChart series={top.series} />
                  </ChartFrame>
                </CardContent>
              </Card>
            )}

            {/* Latest poll CTA */}
            {voice?.latest_poll && (
              <Card data-testid="latest-poll-card">
                <CardContent className="p-5">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1 inline-flex items-center gap-1.5">
                    <Megaphone className="h-3.5 w-3.5 text-accent" /> Latest Poll
                  </p>
                  <p className="font-heading font-semibold text-foreground">{voice.latest_poll.question}</p>
                  <Button asChild size="sm" variant="outline" className="mt-3" data-testid="latest-poll-answer">
                    <Link to={`/polls${muniId ? `?municipality=${muniId}` : ""}`}>Answer it →</Link>
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* WHY PEOPLE CHOSE — privacy-safe comment themes */}
      {themes?.available && themes.themes?.length > 0 && (
        <Card className="mt-6" data-testid="themes-card">
          <CardContent className="p-5 sm:p-6">
            <h2 className="font-heading font-bold text-lg text-foreground inline-flex items-center gap-2 mb-1">
              <MessageSquareText className="h-5 w-5 text-accent" /> Why people chose their answers
            </h2>
            <p className="text-xs text-muted-foreground mb-3" data-testid="themes-reviewed">
              {themes.comments_reviewed} anonymous comments reviewed
            </p>
            <div className="flex flex-wrap gap-1.5" data-testid="themes-list">
              {themes.themes.map((t) => (
                <Badge key={t.theme} variant="outline" className="bg-secondary text-[11px]">
                  {t.theme}: {t.count}
                </Badge>
              ))}
            </div>
            {themes.approved_quotes?.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="approved-quotes">
                {themes.approved_quotes.map((q, i) => (
                  <blockquote key={i} className="text-sm text-foreground border-l-2 border-accent pl-3 italic">
                    “{q}” <span className="not-italic text-[10px] text-muted-foreground">— anonymous participant</span>
                  </blockquote>
                ))}
              </div>
            )}
            <p className="text-[10px] text-muted-foreground mt-3">{themes.note}</p>
          </CardContent>
        </Card>
      )}

      {/* COMPARE AREAS */}
      {muniId && (
        <Card className="mt-6" data-testid="compare-card">
          <CardContent className="p-5 sm:p-6">
            <h2 className="font-heading font-bold text-lg text-foreground inline-flex items-center gap-2 mb-1">
              <GitCompareArrows className="h-5 w-5 text-accent" /> Compare Community Priorities
            </h2>
            <p className="text-xs text-muted-foreground mb-3">
              Compare reported priorities between two areas. This never means one community is
              doing better or worse than another.
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Area A</Label>
                <p className="text-sm font-medium mt-1" data-testid="compare-a-label">{muniName}{cmpAWard ? ` — ${cmpAWard}` : ""}</p>
              </div>
              {eligibleWards.length > 0 && (
                <div className="w-full sm:w-44">
                  <Label className="text-xs text-muted-foreground">Area A ward (optional)</Label>
                  <Select value={cmpAWard || "citywide"} onValueChange={(v) => setCmpAWard(v === "citywide" ? "" : v)}>
                    <SelectTrigger className="mt-1 bg-white" data-testid="compare-a-ward-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="citywide">City-wide</SelectItem>
                      {eligibleWards.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="w-full sm:w-60">
                <Label className="text-xs text-muted-foreground">Area B</Label>
                <Select value={cmpB} onValueChange={setCmpB}>
                  <SelectTrigger className="mt-1 bg-white" data-testid="compare-b-select"><SelectValue placeholder="Choose area" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {cmpAWard && <SelectItem value="citywide">{muniName} — City-wide</SelectItem>}
                    {munis.filter((m) => m.id !== muniId).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button size="sm" disabled={!cmpB || cmpBusy} onClick={runCompare} data-testid="compare-run-button">
                {cmpBusy ? "Comparing…" : "Compare"}
              </Button>
            </div>
            {cmp && (
              <div className="mt-5" data-testid="compare-result">
                {cmp.available ? (
                  <>
                    <p className="text-xs text-muted-foreground mb-2" data-testid="compare-counts">
                      Responses — A ({cmp.a.label}): {cmp.a.response_count} · B ({cmp.b.label}): {cmp.b.response_count}
                    </p>
                    <CompareChart rows={cmp.rows} aLabel={cmp.a.label} bLabel={cmp.b.label} />
                    <p className="text-[10px] text-muted-foreground mt-2">{cmp.note}</p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground" data-testid="compare-insufficient">{cmp.note || "Not enough data yet"}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* WEEKLY PULSE DIGEST — place-based only, simulated email */}
      <Card className="mt-6" data-testid="digest-card">
        <CardContent className="p-5 sm:p-6">
          <h2 className="font-heading font-bold text-lg text-foreground inline-flex items-center gap-2 mb-1">
            <Mail className="h-5 w-5 text-accent" /> Weekly Community Pulse
          </h2>
          <p className="text-xs text-muted-foreground mb-3 max-w-xl">
            Get a weekly summary of what your community is saying — top issues, what's changing and
            this week's question. Personalized by where you live, never by how you answer.{" "}
            <Link to="/community-voice/weekly" className="text-primary underline underline-offset-2 font-medium" data-testid="weekly-preview-link">
              See this week's Pulse on the web first
            </Link>.
          </p>
          <form onSubmit={subscribeDigest} className="flex flex-wrap items-end gap-3">
            <div className="w-full sm:w-64">
              <Label className="text-xs text-muted-foreground">Email</Label>
              <Input type="email" required className="mt-1" placeholder="you@example.com"
                     value={digestEmail} onChange={(e) => setDigestEmail(e.target.value)} data-testid="digest-email-input" />
            </div>
            <div className="w-full sm:w-56">
              <Label className="text-xs text-muted-foreground">My municipality</Label>
              <Select value={digestMuni || undefined} onValueChange={setDigestMuni}>
                <SelectTrigger className="mt-1 bg-white" data-testid="digest-muni-select"><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {munis.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-40">
              <Label className="text-xs text-muted-foreground">My ward (optional)</Label>
              <Input className="mt-1" placeholder="e.g. Ward 8" maxLength={120}
                     value={digestWard} onChange={(e) => setDigestWard(e.target.value)} data-testid="digest-ward-input" />
            </div>
            <Button type="submit" size="sm" disabled={digestBusy || !digestMuni} data-testid="digest-subscribe-button">
              {digestBusy ? "Subscribing…" : "Subscribe"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-10 max-w-3xl" data-testid="voice-disclaimer">
        Community Poll results reflect voluntary participation on WitnessYourVote. They are not a
        scientific opinion poll and should not be treated as representative of every voter in the
        municipality. Poll answers are never connected to voting declarations.
      </p>
      <div className="mt-3">
        <ReportErrorButton context="Community Voice — source material" defaultCategory="outdated_source" />
      </div>
    </div>
  );
}
